import React, { useState } from 'react';
import { 
  RotateCcw, 
  X, 
  Calendar, 
  Clock, 
  Layers, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  ShieldAlert, 
  ArrowRight,
  BookmarkCheck,
  History
} from 'lucide-react';

export default function CheckpointsModal({
  isOpen,
  onClose,
  checkpoints = [],
  onRestore,
  onCreateCheckpoint,
  onDeleteCheckpoint,
  currentProtocolCount = 0
}) {
  const [newCheckpointName, setNewCheckpointName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const isBusy = isCreating || restoringId !== null || deletingId !== null;

  const uniqueCheckpoints = React.useMemo(() => {
    const seen = new Set();
    return (checkpoints || []).filter(cp => {
      if (!cp?.id || seen.has(cp.id)) return false;
      seen.add(cp.id);
      return true;
    });
  }, [checkpoints]);

  if (!isOpen) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isCreating) return;
    if (!newCheckpointName.trim() && currentProtocolCount === 0) return;
    setIsCreating(true);
    try {
      await onCreateCheckpoint(newCheckpointName.trim() || `Manual Checkpoint (${currentProtocolCount} protocols)`);
      setNewCheckpointName('');
      setShowCreateForm(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestoreClick = async (checkpoint) => {
    if (restoringId || isCreating || deletingId) return;
    if (window.confirm(`Restore ${checkpoint.protocolCount} protocols from "${checkpoint.reason}"? (Your current stack will be preserved in a recovery checkpoint if you have active items).`)) {
      setRestoringId(checkpoint.id);
      try {
        await onRestore(checkpoint);
        onClose();
      } finally {
        setRestoringId(null);
      }
    }
  };

  const handleDeleteClick = async (checkpointId) => {
    if (deletingId || isCreating || restoringId) return;
    if (window.confirm("Delete this checkpoint? This cannot be undone.")) {
      setDeletingId(checkpointId);
      try {
        await onDeleteCheckpoint(checkpointId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return isoString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-slate-900 border border-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl p-6 relative overflow-hidden flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-4 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white tracking-tight">
                Protocol Stack Checkpoints
              </h3>
              <p className="text-xs text-slate-400">
                Restore previously cleared stacks or jump back to historical configurations
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {currentProtocolCount > 0 && (
              <button
                type="button"
                onClick={() => setShowCreateForm(prev => !prev)}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold border border-slate-700 transition"
              >
                <Plus className="w-3.5 h-3.5 text-sky-400" />
                <span>Save Checkpoint</span>
              </button>
            )}

            <button
              onClick={onClose}
              disabled={isBusy}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Create Checkpoint Inline Form */}
        {showCreateForm && (
          <form onSubmit={handleCreate} className="p-3.5 bg-slate-950/80 rounded-xl border border-sky-500/30 mb-4 shrink-0 space-y-2 animate-fadeIn">
            <div className="text-xs font-semibold text-sky-300">
              Create New Checkpoint from Current Stack ({currentProtocolCount} protocols)
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newCheckpointName}
                onChange={(e) => setNewCheckpointName(e.target.value)}
                placeholder="Checkpoint label (e.g. Pre-Summer Cut Stack, Baseline TRT+Peptides)"
                className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
              />
              <button
                type="submit"
                disabled={isCreating || restoringId !== null || deletingId !== null}
                className="px-3.5 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-2.5 py-1.5 rounded-xl bg-slate-800 text-slate-400 text-xs hover:text-white"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Checkpoint List */}
        <div className="overflow-y-auto space-y-3 pr-1 flex-1">
          {uniqueCheckpoints.length === 0 ? (
            <div className="text-center py-12 px-4 border border-dashed border-slate-800 rounded-2xl">
              <RotateCcw className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <h4 className="text-sm font-semibold text-slate-300">No Checkpoints Recorded</h4>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Whenever you clear your stack or create a snapshot, it will appear here so you can restore your protocol stack at any time.
              </p>
            </div>
          ) : (
            uniqueCheckpoints.map((cp) => (
              <div 
                key={cp.id}
                className="p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 hover:border-slate-700/80 transition space-y-2.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold text-white">
                        {cp.reason || 'Stack Checkpoint'}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                        {cp.protocolCount || cp.protocols?.length || 0} protocols
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 text-[11px] text-slate-400 mt-1 font-mono">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      <span>{formatDate(cp.timestamp)}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleRestoreClick(cp)}
                      disabled={restoringId === cp.id || isCreating || deletingId === cp.id}
                      className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold border border-emerald-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Restore your stack from this checkpoint"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-emerald-400" />
                      <span>{restoringId === cp.id ? 'Restoring...' : 'Restore Stack'}</span>
                    </button>

                    {onDeleteCheckpoint && (
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(cp.id)}
                        disabled={deletingId === cp.id || isCreating || restoringId === cp.id}
                        className="p-1.5 rounded-lg bg-slate-900 hover:bg-red-500/20 text-slate-500 hover:text-red-400 border border-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={deletingId === cp.id ? "Deleting checkpoint..." : "Delete checkpoint"}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Protocol Preview Tags */}
                {cp.protocols && cp.protocols.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {cp.protocols.slice(0, 8).map((p, idx) => (
                      <span
                        key={`proto-tag-${cp.id}-${idx}`}
                        className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-900 border border-slate-800 text-slate-400"
                      >
                        {p.name} {p.dosageMin ? `(${p.dosageMin}${p.unit || ''})` : ''}
                      </span>
                    ))}
                    {cp.protocols.length > 8 && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-900 border border-slate-800 text-slate-500">
                        +{cp.protocols.length - 8} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer info */}
        <div className="mt-4 pt-3 border-t border-slate-800/80 text-[11px] text-slate-500 flex items-center justify-between shrink-0">
          <span>Checkpoints are backed up in Firestore & LocalStorage</span>
          <span>{uniqueCheckpoints.length} saved</span>
        </div>
      </div>
    </div>
  );
}
